import {
  getFromLocalStorage,
  removeFromLocalStorage,
  saveToLocalStorage,
} from '@/common/utils/localStorage';
import * as Sentry from '@sentry/react';
import { TooltipProvider } from '@shadcn/components/tooltip';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';
import updateLocale from 'dayjs/plugin/updateLocale';
import { StrictMode, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import App from './App';
import './index.css';
import { store } from './redux/store';
import './tailwind.css';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    sendDefaultPii: true,
    environment: process.env.NODE_ENV,

    integrations: [
      Sentry.replayIntegration(),
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayCanvasIntegration(),
    ],

    // 100% sampling in dev, 10% in prod
    tracesSampleRate: process.env.NODE_ENV === 'development' ? 1.0 : 0.1,
    replaysSessionSampleRate:
      process.env.NODE_ENV === 'development' ? 1.0 : 0.1,
    replaysOnErrorSampleRate: 1.0, // Always capture replays on error
  });
}

// DayJS config
dayjs.extend(relativeTime);
dayjs.extend(duration);
dayjs.extend(updateLocale);
dayjs.updateLocale('en', {
  relativeTime: {
    ...dayjs.Ls.en.relativeTime,
    s: 'just now',
    past: (s: string) => (s === 'just now' ? s : `${s} ago`),
  },
});

// Migrate organisation local storage data
try {
  const currentOrganisation = getFromLocalStorage('currentOrganisation');
  const parsedCurrentOrganisation = JSON.parse(currentOrganisation || '{}');
  if (parsedCurrentOrganisation && parsedCurrentOrganisation.id) {
    const cleanId = parsedCurrentOrganisation.id.replace(/[^a-zA-Z0-9]/g, '');
    saveToLocalStorage('currentOrganisationId', cleanId);
  }
  removeFromLocalStorage('currentOrganisation');
  removeFromLocalStorage('organisations');
} catch (error) {
  console.error('Error migrating organisation local storage data:', error);
}

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <StrictMode>
    <TooltipProvider delayDuration={300}>
      <Provider store={store}>
        <App />
      </Provider>
    </TooltipProvider>
  </StrictMode>,
);
